import { fetchApi } from '@/app/api';
import {
  DTO,
  EditableWorkorderFields,
  EditableWorkorderLaborFields,
  EditableWorkorderMaterialFields,
  NewWorkorderLabor,
  NewWorkorderMaterial,
  parseWorkorderDto,
  parseWorkorderLaborDto,
  parseWorkorderMaterialDto,
  Workorder,
  WorkorderLabor,
  WorkorderMaterial,
} from '@certhon/models';
import { map } from 'remeda';

/** API-compatible editable fields (dates as strings) */
export type EditableWorkorderFieldsDto = DTO<EditableWorkorderFields>;
export type EditableWorkorderLaborFieldsDto = DTO<EditableWorkorderLaborFields>;
export type NewWorkorderLaborDto = DTO<NewWorkorderLabor>;

export type WorkorderFilter = Record<string, string | number>;

/**
 * PostgREST filter for the `progress` column selecting the workorders that
 * matter to technicians: planned digital bons and finished ones. Concept and
 * cancelled bons (and non-digital ones) are hidden everywhere the user is
 * looking at "their" work. The all-workorders list applies the same rule.
 */
export const ACTIVE_PROGRESS_FILTER = 'in.(scheduled,completed)';

// ============================================================================
// Workorders
// ============================================================================

export async function fetchWorkorders(
  filter?: WorkorderFilter,
): Promise<Workorder[]> {
  const workorders = await fetchApi('/workorders', filter);
  return map(workorders, parseWorkorderDto);
}

export async function fetchWorkorder(recnum: number): Promise<Workorder> {
  const results = await fetchApi('/workorders', {
    recnum: `eq.${recnum}`,
  });
  if (!results.length) {
    throw new Error('Could not find Workorder');
  }
  return parseWorkorderDto(results[0] as DTO<Workorder>);
}

export async function updateWorkorder(
  recnum: number,
  data: EditableWorkorderFieldsDto,
): Promise<Workorder> {
  const results = await fetchApi(
    '/workorders',
    { recnum: `eq.${recnum}` },
    {
      method: 'PATCH',
      body: JSON.stringify(data),
    },
  );
  if (!results.length) {
    throw new Error('Failed to update workorder');
  }
  return parseWorkorderDto(results[0] as DTO<Workorder>);
}

/**
 * Complete a workorder without signature.
 * This triggers backend logic: copy prematerials, build pdf_task snapshot, etc.
 */
export async function completeWorkorder(
  recnum: number,
  email?: string,
): Promise<void> {
  const response = await fetch(`/api/workorders/${recnum}/complete`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email }),
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || 'Failed to complete workorder');
  }
}

/**
 * Sign and complete a workorder with customer signature.
 * Triggers backend logic and sends email notification to customer.
 */
export async function signWorkorder(
  recnum: number,
  data: { signature_data: string; email: string },
): Promise<void> {
  const response = await fetch(`/api/workorders/${recnum}/sign`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const responseData = await response.json().catch(() => ({}));
    throw new Error(responseData.error || 'Failed to sign workorder');
  }
}

// ============================================================================
// Labor
// ============================================================================

export async function fetchWorkorderLabor(
  workorderRecnum: number,
): Promise<WorkorderLabor[]> {
  const labor = await fetchApi('/workorder_labor', {
    workorder_id: `eq.${workorderRecnum}`,
    order: 'date_start.asc',
  });
  return map(labor, parseWorkorderLaborDto);
}

export async function fetchLaborEntry(recnum: number): Promise<WorkorderLabor> {
  const results = await fetchApi('/workorder_labor', {
    recnum: `eq.${recnum}`,
  });
  if (!results.length) {
    throw new Error('Could not find labor entry');
  }
  return parseWorkorderLaborDto(results[0] as DTO<WorkorderLabor>);
}

export async function createLaborEntry(
  data: NewWorkorderLaborDto,
): Promise<WorkorderLabor> {
  const results = await fetchApi('/workorder_labor', undefined, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  if (!results.length) {
    throw new Error('Failed to create labor entry');
  }
  return parseWorkorderLaborDto(results[0] as DTO<WorkorderLabor>);
}

export async function updateLaborEntry(
  recnum: number,
  data: EditableWorkorderLaborFieldsDto,
): Promise<WorkorderLabor> {
  const results = await fetchApi(
    '/workorder_labor',
    { recnum: `eq.${recnum}` },
    {
      method: 'PATCH',
      body: JSON.stringify(data),
    },
  );
  if (!results.length) {
    throw new Error('Failed to update labor entry');
  }
  return parseWorkorderLaborDto(results[0] as DTO<WorkorderLabor>);
}

export async function deleteLaborEntry(recnum: number): Promise<void> {
  await fetchApi(
    '/workorder_labor',
    { recnum: `eq.${recnum}` },
    { method: 'DELETE' },
  );
}

// ============================================================================
// Materials
// ============================================================================

export async function fetchWorkorderMaterials(
  workorderRecnum: number,
): Promise<WorkorderMaterial[]> {
  const materials = await fetchApi('/workorder_materials', {
    workorder_id: `eq.${workorderRecnum}`,
    order: 'recnum.asc',
  });
  return map(materials, parseWorkorderMaterialDto);
}

export async function fetchMaterialEntry(
  recnum: number,
): Promise<WorkorderMaterial> {
  const results = await fetchApi('/workorder_materials', {
    recnum: `eq.${recnum}`,
  });
  if (!results.length) {
    throw new Error('Could not find material entry');
  }
  return parseWorkorderMaterialDto(results[0] as DTO<WorkorderMaterial>);
}

export async function createMaterialEntry(
  data: NewWorkorderMaterial,
): Promise<WorkorderMaterial> {
  const results = await fetchApi('/workorder_materials', undefined, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  if (!results.length) {
    throw new Error('Failed to create material entry');
  }
  return parseWorkorderMaterialDto(results[0] as DTO<WorkorderMaterial>);
}

export async function updateMaterialEntry(
  recnum: number,
  data: EditableWorkorderMaterialFields,
): Promise<WorkorderMaterial> {
  const results = await fetchApi(
    '/workorder_materials',
    { recnum: `eq.${recnum}` },
    {
      method: 'PATCH',
      body: JSON.stringify(data),
    },
  );
  if (!results.length) {
    throw new Error('Failed to update material entry');
  }
  return parseWorkorderMaterialDto(results[0] as DTO<WorkorderMaterial>);
}

export async function deleteMaterialEntry(recnum: number): Promise<void> {
  await fetchApi(
    '/workorder_materials',
    { recnum: `eq.${recnum}` },
    { method: 'DELETE' },
  );
}
